import React from 'react'
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tag,
  Tooltip,
} from '@octanner/prism-core'
import Timeline from '@mui/lab/Timeline'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineContent from '@mui/lab/TimelineContent'
import TimelineDot from '@mui/lab/TimelineDot'
import TimelineOppositeContent, {
  timelineOppositeContentClasses,
} from '@mui/lab/TimelineOppositeContent'
import { ArrowDown, InformationCircle } from '@octanner/prism-icons'
import { CelebrationTimelineEvent } from '../../hooks/useGetCelebrationByIdentity'
import {
  formatDateString,
  getDisplayDataForScheduledTask,
  getHighestPriorityTask,
  getPrivacyTooltipText,
  getScheduleTaskStatus,
  getStatusProps,
} from './utils'
import ScheduledTaskSummary from './ScheduledTaskSummary'
import { tannerGray } from '@octanner/prism-core/ThemeProvider/colors'

export type CelebrationTimelineViewProps = {
  celebrationTimeline: CelebrationTimelineEvent[]
  celebrationCreationDate: string
  hasPrivateCelebrations: boolean
}

const CelebrationTimelineView: React.FC<CelebrationTimelineViewProps> = ({
  celebrationTimeline,
  celebrationCreationDate,
  hasPrivateCelebrations,
}) => {
  const celebrationCreatedEvent: CelebrationTimelineEvent = {
    name: 'Celebration creation',
    description:
      'Creation of celebration event and start of celebration window',
    activeWindowStart: '2023-01-30T10:00:00Z',
    activeWindowEnd: '2023-01-30T14:00:00Z',
    actionRespectsPrivacy: false,
    group: {
      id: '1',
      code: 'test-group',
      description: 'Test Group',
      scheduleGroupCategory: 'Test Category',
      numReminders: 1,
    },
    scheduledTasks: [
      {
        actionType: 'SYS_COMPLETED_ACTION',
        taskType: 'SYS_COMPLETED_ACTION',
        startTime: celebrationCreationDate,
        endTime: celebrationCreationDate,
        lastExecution: {
          executionTime: celebrationCreationDate,
          status: 'SUCCESS',
        },
        cancelledTime: null,
      },
    ],
  }
  const sortedSchedules = [...celebrationTimeline].sort(
    (a, b) =>
      new Date(a.activeWindowStart).getTime() -
      new Date(b.activeWindowEnd).getTime()
  )
  sortedSchedules.unshift(celebrationCreatedEvent)
  return (
    <Box>
      <Typography variant="h4" sx={{ mt: '24px', mb: '16px' }}>
        Celebration timeline
      </Typography>
      <Box
        sx={{
          border: '1px solid #E1E1EB',
          borderRadius: '8px',
          mb: '70px',
          minHeight: '-webkit-fill-available',
        }}
      >
        <Timeline
          sx={{
            [`& .${timelineOppositeContentClasses.root}`]: { flex: 0.2 },
            p: '24px',
            m: 0,
          }}
        >
          {sortedSchedules.map((schedule, index) => {
            const scheduledTaskWithStatuses = schedule.scheduledTasks.map(
              (scheduledTask) => {
                return {
                  ...scheduledTask,
                  status: getScheduleTaskStatus(
                    scheduledTask,
                    schedule.actionRespectsPrivacy,
                    hasPrivateCelebrations
                  ),
                }
              }
            )

            const highestPriorityTask = getHighestPriorityTask(
              scheduledTaskWithStatuses
            )

            const status = highestPriorityTask.status

            const displayDate =
              getDisplayDataForScheduledTask(highestPriorityTask)

            const statusProps = getStatusProps(status)

            const StatusIcon = statusProps?.icon ?? null
            const timelineDotColor = statusProps?.timelineDotColor ?? undefined
            const timelineConnectorColor =
              statusProps?.timelineConnectorColor ?? undefined
            const isFailed = status === 'Failed'
            return (
              <TimelineItem key={index} sx={{ minHeight: 'unset' }}>
                <TimelineOppositeContent
                  color="textSecondary"
                  sx={{ p: '8px 16px', maxWidth: '133px' }}
                  data-testid={`timeline-opposite-date-${index}`}
                >
                  {displayDate}
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot
                    sx={{
                      height: '16px',
                      width: '16px',
                      p: 0,
                      justifyContent: 'center',
                      alignItems: 'center',
                      my: '12px',
                      boxShadow: 'none',
                      margin: '12px 0px',
                      backgroundColor: !isFailed
                        ? `${timelineDotColor} !important`
                        : 'transparent !important',
                    }}
                  >
                    {StatusIcon && (
                      <StatusIcon
                        sx={{
                          height: isFailed ? '16px' : '10px',
                          width: isFailed ? '16px' : '10px',
                          color: isFailed ? timelineDotColor : 'none',
                        }}
                      />
                    )}
                  </TimelineDot>

                  <TimelineConnector
                    sx={{
                      width: '1px',
                      backgroundColor: `${timelineConnectorColor} !important`,
                    }}
                  />
                </TimelineSeparator>
                <TimelineContent
                  sx={{
                    p: '0px 16px',
                  }}
                >
                  <Box
                    sx={{
                      p: '8px 0px',
                      borderBottom: '1px solid #E1E1EB !important',
                    }}
                  >
                    <Accordion
                      sx={{
                        border: 'none !important',
                        minHeight: 'unset !important',
                        height: 'unset !important',
                      }}
                    >
                      <AccordionSummary
                        sx={{
                          alignItems: 'start',
                          '.MuiAccordionSummary-content': {
                            margin: '0 !important',
                          },
                          minHeight: 'unset !important',
                          height: 'unset !important',
                        }}
                        expandIcon={<ArrowDown sx={{ m: '8px' }} />}
                        aria-controls="panel1d-content"
                        id="panel1d-header"
                      >
                        <Box>
                          <Typography variant="h4">
                            {schedule.name}{' '}
                            {schedule.actionRespectsPrivacy ? (
                              <Tag variant="archived" color={tannerGray[800]}>
                                {' '}
                                Privacy
                                <Tooltip
                                  title={getPrivacyTooltipText(status)}
                                  placement="top"
                                  style={{ marginLeft: 8 }}
                                >
                                  <InformationCircle
                                    sx={{ color: tannerGray[600] }}
                                  />
                                </Tooltip>
                              </Tag>
                            ) : null}
                          </Typography>
                          <Typography variant="body2">
                            {schedule.description}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ mt: 1 }}
                            data-testid={`active-window-${index}`}
                          >
                            Active window:{' '}
                            {formatDateString(schedule.activeWindowStart)} -{' '}
                            {formatDateString(schedule.activeWindowEnd)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            padding: '0px 40px',
                            alignItems: 'center',
                            alignSelf: 'stretch',
                            justifyContent: 'space-between',
                            mt: 4,
                          }}
                        >
                          {scheduledTaskWithStatuses.map((task, taskIndex) => (
                            <ScheduledTaskSummary
                              key={taskIndex}
                              scheduledTask={task}
                              timelineIndex={index}
                              taskIndex={taskIndex}
                            />
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                </TimelineContent>
              </TimelineItem>
            )
          })}
        </Timeline>
      </Box>
    </Box>
  )
}

export default CelebrationTimelineView
